/**
 * The three-screen booking flow as a pure reducer: Visit (who and what),
 * Time (a 5-minute hold, OB-03), Details (who to book and consents), then the
 * confirmation. Network calls live in the screens; this only decides what the
 * visitor sees next, so it is unit tested without a DOM.
 */
import type { BookingFor, Hold, SubmitResult } from '@velora/contracts/booking';

export type Step = 'visit' | 'time' | 'details' | 'done';
export const STEPS: ReadonlyArray<{ step: Exclude<Step, 'done'>; label: string }> = [
  { step: 'visit', label: 'Visit' },
  { step: 'time', label: 'Time' },
  { step: 'details', label: 'Details' },
];

/** The person booking, kept for a second family member (OB-20). */
export interface Booker {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
}

/** The visit just booked, for back-to-back family suggestions (OB-20). */
export interface FamilyAnchor {
  reference: string;
  start: string;
  end: string;
  providerName: string;
  patientFirstName: string;
  /** Provider of the first visit, when the hold told us. */
  providerId: string | null;
}

export interface FlowState {
  step: Step;
  patientKind: 'new' | 'existing';
  bookingFor: BookingFor;
  typeId: string | null;
  /** null = first available with anyone. */
  providerId: string | null;
  hold: Hold | null;
  /** Set when a hold ran out or a time was taken; shown once on the Time screen. */
  notice: string | null;
  result: SubmitResult | null;
  family: FamilyAnchor | null;
  booker: Booker | null;
  /** Bookings made in this visit (the first and any family members). */
  booked: SubmitResult[];
}

export type FlowAction =
  | { type: 'patientKind'; kind: 'new' | 'existing' }
  | { type: 'bookingFor'; value: BookingFor }
  | { type: 'chooseType'; typeId: string }
  | { type: 'provider'; providerId: string | null }
  | { type: 'held'; hold: Hold }
  | { type: 'holdLost'; message: string }
  | { type: 'back' }
  | { type: 'goto'; step: Exclude<Step, 'done'> }
  | {
      type: 'submitted';
      result: SubmitResult;
      patientFirstName: string;
      booker: Booker | null;
    }
  | { type: 'bookAnother' }
  | { type: 'identified' }
  | { type: 'signedOut' };

export function initialFlow(opts: {
  identified: boolean;
  typeId?: string | null;
  typeVisible?: boolean;
}): FlowState {
  const jump = Boolean(opts.typeId && opts.typeVisible);
  return {
    step: jump ? 'time' : 'visit',
    patientKind: opts.identified ? 'existing' : 'new',
    bookingFor: 'self',
    typeId: jump ? (opts.typeId ?? null) : null,
    providerId: null,
    hold: null,
    notice: null,
    result: null,
    family: null,
    booker: null,
    booked: [],
  };
}

export function flowReducer(s: FlowState, a: FlowAction): FlowState {
  switch (a.type) {
    case 'patientKind':
      return { ...s, patientKind: a.kind };
    case 'bookingFor':
      return { ...s, bookingFor: a.value };
    case 'chooseType':
      return {
        ...s,
        typeId: a.typeId,
        // A different visit type keeps the provider filter only when it is "anyone".
        providerId: s.typeId === a.typeId ? s.providerId : null,
        hold: null,
        notice: null,
        step: 'time',
      };
    case 'provider':
      return { ...s, providerId: a.providerId };
    case 'held':
      return { ...s, hold: a.hold, notice: null, step: 'details' };
    case 'holdLost':
      return { ...s, hold: null, notice: a.message, step: 'time' };
    case 'back':
      if (s.step === 'details') return { ...s, step: 'time' };
      if (s.step === 'time') return { ...s, step: 'visit', hold: null, notice: null };
      return s;
    case 'goto': {
      const order: Step[] = ['visit', 'time', 'details'];
      // Only backwards, and never to Details without a hold.
      if (order.indexOf(a.step) >= order.indexOf(s.step)) return s;
      if (a.step === 'visit') return { ...s, step: 'visit', hold: null, notice: null };
      return { ...s, step: a.step };
    }
    case 'submitted':
      return {
        ...s,
        step: 'done',
        result: a.result,
        hold: null,
        notice: null,
        booker: a.booker ?? s.booker,
        booked: [...s.booked, a.result],
        family: {
          reference: a.result.reference,
          start: a.result.start,
          end: a.result.end,
          providerName: a.result.providerName,
          patientFirstName: a.patientFirstName,
          providerId: s.hold?.providerId ?? null,
        },
      };
    case 'bookAnother':
      return {
        ...s,
        step: 'visit',
        // The next person is a family member the visitor is booking for.
        bookingFor: 'dependent',
        // A child of an existing patient may still be new to the office.
        patientKind: 'new',
        providerId: null,
        hold: null,
        notice: null,
        result: null,
      };
    case 'identified':
      return { ...s, patientKind: 'existing', bookingFor: 'self' };
    case 'signedOut':
      return { ...s, patientKind: 'new', typeId: null, hold: null, step: 'visit' };
  }
}

/**
 * Whether the patient's own details come from the identity token. The token
 * is single-use (OB-09): after a booking the app drops it, so a second family
 * member is always booked with typed details.
 */
export function usesIdentity(s: FlowState, identified: boolean): boolean {
  return identified && s.bookingFor === 'self';
}
